import { Option } from '@sapphire/result';

import { BasePayloadMapper } from '@ps/utils';
import {
  AdvancedFeasibilityFilters,
  ConditionLabels,
  NonQuotaTargeting,
  QUALIFICATION_CODE,
  Quota,
  SurveyMetadataForm,
  SurveyPricingSnapshot,
  SurveyQualification,
  LOCATION_QUALIFICATION_NAME,
  toConditionNamePairs,
} from '@ps/survey-utils';

import { StandardSurveyPayloadDto } from '../dtos/survey.dto';
import { SURVEY_CATEGORY_CODE } from '../../utils';
import { SurveyQuotaPayloadMapper } from './survey-quota-payload.mapper';
import { SurveyFeasibilityMapper } from './domain/survey-feasibility.mapper';
import { childAgeGenderConditionNameLookup } from './domain/child-age-gender/child-age-gender-quota.mapper';
import {
  QualificationPayloadBaseDto,
  QualificationPayloadDto,
  SelectableQualificationPayloadDto,
} from '../dtos/survey-qualification.dto';
import { QuotaPayloadDto } from '../dtos/survey-quota.dto';

// A PUT replaces both arrays wholesale, so the caller always supplies the
// survey's complete current qualifications and quotas, never a partial diff.
export interface StandardSurveyTargetingInput {
  qualifications: SurveyQualification[];
  quotas: Quota[];
}

// Canonical "what to write for this survey" shape, passed unchanged through
// SurveyService → SurveyApi → this mapper — nothing repacks it along the way.
export interface StandardSurveyPayloadInput {
  metadata: SurveyMetadataForm;
  companyId: number;
  userId: number;
  pricing: SurveyPricingSnapshot;
  targeting: StandardSurveyTargetingInput;
  advancedFeasibilityFilters?: AdvancedFeasibilityFilters;
}

export class StandardSurveyPayloadMapper extends BasePayloadMapper<
  StandardSurveyPayloadInput,
  StandardSurveyPayloadDto
> {
  private readonly _surveyFeasibilityMapper = new SurveyFeasibilityMapper();

  public toPayload({
    metadata,
    companyId,
    userId,
    pricing,
    targeting,
    advancedFeasibilityFilters,
  }: StandardSurveyPayloadInput): StandardSurveyPayloadDto {
    const payload: StandardSurveyPayloadDto = {
      survey_title: metadata.title,
      buyer_company: companyId,
      buyer_user_id: userId,
      survey_category_code: SURVEY_CATEGORY_CODE,
      survey_localization: this._localization(metadata),
      completes_required: metadata.completes ?? 0,
      expected_loi: metadata.loi ?? 0,
      expected_ir: metadata.incidence ?? 0,
      field_time: metadata.fieldTime ?? 0,
      click_balancing: this._clickBalancing(metadata),
      feasibility_cpi: pricing.feasibilityCpi,
      blend_applied: pricing.blendApplied,
    };

    const nonQuotaTargeting = this._nonQuotaTargeting(targeting.quotas);
    const quotas = this._mapQuotas(
      targeting.quotas,
      nonQuotaTargeting,
      childAgeGenderConditionNameLookup(targeting.qualifications),
    );
    const qualifications = this._mapQualifications(
      targeting.qualifications,
      nonQuotaTargeting,
      quotas,
    );

    return {
      ...payload,
      ...Option.from(pricing.surveyPauseThreshold).mapOr({}, (value) => ({
        survey_pause_threshold: value,
      })),
      ...Option.from(pricing.offerPrice).mapOr({}, (offerPrice) => ({
        offer_price: offerPrice,
      })),
      ...(qualifications.length ? { qualifications } : {}),
      ...(quotas.length ? { quotas } : {}),
      ...this._advancedFilters(advancedFeasibilityFilters),
    };
  }

  private _advancedFilters(
    filters?: AdvancedFeasibilityFilters,
  ): Partial<StandardSurveyPayloadDto> {
    return Option.from(filters).mapOr({}, (_filters) => ({
      feasibility_exclusions:
        this._surveyFeasibilityMapper.mapToFeasibilityExclusions(_filters),
      survey_grouping:
        this._surveyFeasibilityMapper.mapToSurveyGrouping(_filters),
      incl_excl: this._surveyFeasibilityMapper.mapToInclExcl(_filters),
      ref_list: this._surveyFeasibilityMapper.mapToRefList(_filters),
    }));
  }

  private _qualificationPayload(
    qualification: SurveyQualification,
    nonQuotaTargeting: NonQuotaTargeting,
    quotas: QuotaPayloadDto[],
  ): QualificationPayloadDto {
    const base: QualificationPayloadBaseDto = {
      qualification_name: this._qualificationName(qualification),
      qualification_code: qualification.qualificationCode,
      non_quota_targeted: nonQuotaTargeting.flagForQuotaRow(
        qualification.qualificationCode,
      ),
    };

    if (qualification.isRangeType) {
      return { ...base, range_sets: qualification.rangeSets };
    }

    if (
      qualification.qualificationCode === QUALIFICATION_CODE.CHILDREN_LEGACY
    ) {
      return this._childAgeGenderQualificationPayload(base, quotas);
    }

    return {
      ...base,
      condition_codes: qualification.conditionCodes,
      // No re-filter: `data` already holds exactly the selected conditions (see
      // selectedConditions, which is where that invariant is produced).
      condition_names: toConditionNamePairs(qualification.data),
    };
  }

  // The 218 flag's own condition_codes (['111']/['112'] — no/have children) carry none of the
  // real age+gender granularity; that only exists per-quota, since the buyer configures age/
  // gender on each children quota row, never on the qualification itself. So this entry is built
  // from the union of what the already-converted 232 quota criteria carry (see
  // toChildAgeGenderCriterion) rather than from the qualification's own selection — the same
  // real 232 condition codes/names quotas[] just sent, deduped across every children quota row.
  private _childAgeGenderQualificationPayload(
    base: QualificationPayloadBaseDto,
    quotas: QuotaPayloadDto[],
  ): SelectableQualificationPayloadDto {
    const conditionNameByCode = new Map(
      quotas
        .flatMap((quota) => quota.criteria)
        .filter(
          (criterion) =>
            criterion.qualification_code ===
            QUALIFICATION_CODE.CHILD_AGE_GENDER,
        )
        .flatMap((criterion) =>
          criterion.condition_codes.map((code, index): [string, string] => [
            code,
            criterion.condition_names[index],
          ]),
        ),
    );

    return {
      ...base,
      qualification_code: QUALIFICATION_CODE.CHILD_AGE_GENDER,
      condition_codes: [...conditionNameByCode.keys()],
      condition_names: [...conditionNameByCode.entries()].map(([id, name]) => ({
        [id]: name,
      })),
    };
  }

  // Location qualifications all share the single display name "Location", so it cannot identify
  // which one the payload is for — their key is sent instead. Every other qualification sends
  // its own name.
  private _qualificationName(qualification: SurveyQualification): string {
    return qualification.qualificationName === LOCATION_QUALIFICATION_NAME
      ? qualification.key
      : qualification.qualificationName;
  }

  private _localization(metadata: SurveyMetadataForm): string {
    const countryCode = metadata.selectedCountries[0];
    const languageShortCode = metadata.selectedLanguages[0];
    if (!countryCode || !languageShortCode) {
      return '';
    }
    return `${languageShortCode}_${countryCode}`;
  }

  private _clickBalancing(metadata: SurveyMetadataForm): number {
    return metadata.clickBalancing ? 1 : 0;
  }

  // A default qualification the buyer never touched has nothing to save; a
  // default qualification with a quota built on it does.
  private _mapQualifications(
    qualifications: SurveyQualification[],
    nonQuotaTargeting: NonQuotaTargeting,
    quotas: QuotaPayloadDto[],
  ): QualificationPayloadDto[] {
    return qualifications
      .filter((qualification) => !qualification.isDefault)
      .map((qualification) =>
        this._qualificationPayload(qualification, nonQuotaTargeting, quotas),
      );
  }

  private _mapQuotas(
    quotas: Quota[],
    nonQuotaTargeting: NonQuotaTargeting,
    childAgeGenderConditionNameByCode: ConditionLabels,
  ): QuotaPayloadDto[] {
    const quotaMapper = new SurveyQuotaPayloadMapper(
      nonQuotaTargeting,
      childAgeGenderConditionNameByCode,
    );
    // A quota that cannot be mapped (an in-progress children row) contributes nothing rather
    // than blocking the save — the drawer's own validation is what reports it to the buyer.
    return quotas.flatMap((quota) =>
      quotaMapper.toPayload(quota).match({
        ok: (payload) => [payload],
        err: () => [],
      }),
    );
  }

  // Same per-quota qualification-code grouping as the feasibility side's TargetedQuota.
  private _nonQuotaTargeting(quotas: Quota[]): NonQuotaTargeting {
    return NonQuotaTargeting.from(
      quotas.map(
        (quota) =>
          new Set(
            quota.criteria.map((criterion) => criterion.qualificationCode),
          ),
      ),
    );
  }
}
